import { useRef, useState } from "react";
import { Dialog } from "@mui/material";
import I18n from "common/i18n";
import { CloseBtn, FadeTransition } from "common/elements";
import { btnDelay, useScale, formatDate, usePlaySound } from "common/utils";
import { Header } from "features/parent";
import { AppUser } from "features/user/types";
import { UserStatus } from "./UserStatus";

export const UserList = ({
  visible,
  close,
  children,
  lastActiveUserUid,
  onUserSelect,
}: {
  visible: boolean;
  close: () => void;
  children: AppUser[];
  lastActiveUserUid: string | null;
  onUserSelect: (user: AppUser) => () => void;
}) => {
  const { scale } = useScale();
  const playSound = usePlaySound();
  const ref = useRef<HTMLDivElement>(null);
  const [selectedUser, setSelectedUser] = useState<AppUser | null>(children[0]);

  const handleClose = () => {
    btnDelay(() => {
      playSound();
      close();
    });
  };

  const handleUserSelect = (user: AppUser) => () => {
    btnDelay(() => {
      playSound();
      setSelectedUser(user);
    });
  };

  return (
    <Dialog
      fullScreen
      maxWidth={false}
      open={visible}
      sx={{ zIndex: 100000 }}
      TransitionComponent={FadeTransition}
      PaperProps={{ style: { backgroundColor: "transparent" } }}
      transitionDuration={{ enter: 1000, exit: 500 }}
      onClose={handleClose}
    >
      <div className="flex-col-center !absolute inset-0 bg-textcolor-black/50 w-full h-full">
        <div
          className="flex-col-view items-center w-[1180px] h-[800px] rounded-[8px] border-[8px] border-solid border-white bg-white"
          style={{ transform: `scale(${scale})` }}
        >
          <CloseBtn close={handleClose} />
          <div className="flex-col-view items-center w-full h-full rounded-[4px] bg-beige-60">
            <Header title={I18n.t("MSG_USER_LIST")} />

            <div className="flex-col-center w-full h-[630px] pointer-events-auto">
              <div className="flex-col-center w-full h-full p-4 pb-0">
                <div className="flex-row-el w-full flex-grow overflow-hidden gap-2">
                  <div className="flex-col-el justify-start">
                    <div className="flex-col-el items-center justify-start min-w-[362px] invisible-scrollbar">
                      {children.map((user) => (
                        <div
                          key={user.uid}
                          onClick={handleUserSelect(user)}
                          className={`flex-row-el items-center justify-start relative p-4 rounded-2xl cursor-pointer ${
                            selectedUser?.uid === user.uid ? "bg-green-60" : "bg-transparent"
                          }`}
                        >
                          {user.uid === lastActiveUserUid && (
                            <div
                              className="absolute top-2 right-2 flex-row-el items-center gap-1 bg-green-60 rounded-full px-3"
                              style={{
                                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)",
                              }}
                            >
                              <span className="material-symbols-outlined !text-[18px] text-white">
                                history
                              </span>
                              <p className="text-white text-sm leading-none">
                                {I18n.t("MSG_USER_LATEST")}
                              </p>
                            </div>
                          )}
                          <div className="flex-col-center">
                            <div
                              className="flex-col-center bg-white rounded-full w-[80px] h-[80px]"
                              style={{
                                boxShadow:
                                  "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                              }}
                            >
                              <div className="flex-col-center w-[85%] h-[85%] rounded-full overflow-hidden">
                                <img
                                  key={user.icon}
                                  alt="プレイヤープロフィール画像"
                                  src={user.icon}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            </div>
                          </div>

                          <div className="flex-col-el w-[250px] px-4">
                            <p className="text-body text-left text-textcolor-black text-font-size-body2-narrow w-full text-body-ellipsis">
                              {user.display_name}
                            </p>
                            <p className="text-body text-left text-gray-80 text-font-caption2-narrow">
                              {I18n.t("MSG_SETTING_PARENTS_USER_KIDS_PROFILE_CREATED_AT")}:
                              {formatDate(user.created_at)}
                            </p>
                            <p className="text-body text-left text-gray-80 text-font-caption2-narrow">
                              {I18n.t("MSG_SETTING_PARENTS_USER_PROFILE_LATEST_AT")}:
                              {formatDate(user.last_sign_in_time)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {selectedUser && (
                    <div className="flex-col-center flex-1 gap-5 bg-white/40 rounded-2xl">
                      <div ref={ref} className="flex-col-el visible-scrollbar w-full py-14">
                        <div className="flex-row-el flex-center w-full gap-4 mb-8">
                          <p className="text-body text-[20px] text-textcolor-black text-right leading-[30px]">
                            {I18n.t("MSG_USER_PROFILE_FIELD_NAME")}
                          </p>

                          <div
                            className="flex-col-el flex-center px-6 py-4 bg-beige-10 rounded-[32px] text-textcolor-black"
                            style={{
                              boxShadow:
                                "-1px 1px 1px rgba(63, 74, 97, 0.15), 1px 1px 3px rgba(63, 74, 97, 0.25)",
                            }}
                          >
                            <p className="text-body text-2xl text-body-ellipsis w-full">
                              {selectedUser.display_name}
                            </p>
                          </div>
                        </div>

                        <UserStatus selectedUser={selectedUser} />

                        <div className="flex-col-center mt-8 upsilon">
                          <button
                            onClick={onUserSelect(selectedUser)}
                            className="btn btn-primary flex-row-el !px-6 !py-4"
                          >
                            <div className="flex-row-center">
                              <span className="material-symbols-outlined !text-6xl text-white">
                                play_arrow
                              </span>
                              <p className="text-body text-white text-4xl px-[8px]">
                                {I18n.t("MSG_USER_START")}
                              </p>
                            </div>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Dialog>
  );
};
